// File: frontend/src/pages/messages/MessagesPage.tsx
import React, { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { ConversationDto, MessageSearchResultDto } from "../../models/MessageModel";
import { getConversations, getConversationWith, searchInbox } from "../../services/messageService";
import { onMessageEvent, dispatchMessageEvent } from "../../services/websocketService";
import { ConversationList, ConversationSearchState } from "../../components/messages/ConversationList";
import { ConversationView } from "../../components/messages/ConversationView";
import { NewMessageModal } from "../../components/messages/NewMessageModal";
import { CONVERSATIONS_PER_PAGE } from "../../constants/settings.constants";
import "./MessagesPage.css";

// Minimum characters required before issuing an inbox search request
const SEARCH_MIN_CHARS = 2;

/**
 * Pending recipient info when starting a new conversation
 * that doesn't exist yet in the database.
 */
interface PendingRecipient {
  userId: number;
  username: string;
  role: string;
}

/**
 * Main messaging page layout.
 * Desktop: conversation list on the left, active conversation on the right.
 * Mobile: shows one panel at a time.
 */
export const MessagesPage: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const location  = useLocation();

  const [conversations, setConversations] = useState<ConversationDto[]>([]);
  const [selectedConversationId, setSelectedConversationId] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [showNewMessage, setShowNewMessage] = useState(false);
  // Mobile: track which panel is visible
  const [mobileShowChat, setMobileShowChat] = useState(false);
  // Track pending new conversation (user selected but no conversation yet)
  const [pendingRecipient, setPendingRecipient] = useState<PendingRecipient | null>(null);

  /*
   * Bug fix: when a user soft-deletes a conversation and then re-opens it
   * via the modal, the backend returns the existing conversationId but the
   * conversation is no longer in the local `conversations` array (it was
   * removed by handleConversationDeleted). So `selectedConversation` lookup
   * returns undefined and showChat is false — nothing renders.
   *
   * Solution: store the conversationId directly alongside the recipient info
   * so ConversationView always gets the correct ID even before the list reloads.
   */
  const [explicitConversationId, setExplicitConversationId] = useState<number | null>(null);

  /*
   * Draft text handed over by the caller through router state (currently the
   * entry menu's "send message" action, which pre-fills a reference to the
   * entry). It is passed down to ConversationView once and cleared as soon as
   * ConversationView has copied it into its input box, so switching between
   * conversations afterwards never re-injects it.
   */
  const [initialDraft, setInitialDraft] = useState<string | null>(null);

  // ─── Inbox-wide search state ─────────────────────────────────────────────
  // Lifted here so ConversationList can render results AND selecting a result
  // can open the corresponding conversation in the right panel.
  const [searchInput, setSearchInput] = useState("");
  const [isSearchMode, setIsSearchMode] = useState(false);
  const [searchResults, setSearchResults] = useState<MessageSearchResultDto[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [searchTotalItems, setSearchTotalItems] = useState(0);
  const [searchPage, setSearchPage] = useState(0);
  const [searchTotalPages, setSearchTotalPages] = useState(0);
  // Active query = the query that produced the current results (used for pagination)
  const [activeSearchQuery, setActiveSearchQuery] = useState("");

  // Load conversation list
  const loadConversations = useCallback(async (pageNum: number = 0) => {
    if (!user) return;
    try {
      setLoading(true);
      const response = await getConversations(pageNum, CONVERSATIONS_PER_PAGE);
      if (pageNum === 0) {
        setConversations(response.content);
      } else {
        setConversations((prev) => [...prev, ...response.content]);
      }
      setTotalPages(response.totalPages);
      setPage(pageNum);
    } catch (error) {
      console.error("Failed to load conversations:", error);
    } finally {
      setLoading(false);
    }
  }, [user]);

  // Initial load
  useEffect(() => {
    loadConversations(0);
  }, [loadConversations]);

  // Auto-open conversation when navigated from the Online Users modal message button.
  // The modal passes { recipientId, recipientUsername, recipientRole } via location.state.
  useEffect(() => {
    const state = location.state as {
      recipientId?: number;
      recipientUsername?: string;
      recipientRole?: string;
      draftText?: string;
    } | null;

    if (!state?.recipientId || !state?.recipientUsername) return;

    // Clear state so a back-navigation doesn't re-trigger this
    window.history.replaceState({}, "");

    const { recipientId, recipientUsername, recipientRole, draftText } = state;

    // Pre-fill the composer when the caller supplied a draft (e.g. entry reference)
    if (draftText) {
      setInitialDraft(draftText);
    }

    const openConversation = async () => {
      try {
        const { conversationId } = await getConversationWith(recipientId);
        const recipientInfo = {
          userId:   recipientId,
          username: recipientUsername,
          role:     recipientRole ?? "ROOKIE",
        };
        handleConversationStarted(conversationId, recipientInfo);
      } catch {
        // On error fall back to opening new message modal pre-filled
        setPendingRecipient({
          userId:   recipientId,
          username: recipientUsername,
          role:     recipientRole ?? "ROOKIE",
        });
        setSelectedConversationId(null);
        setExplicitConversationId(null);
        setMobileShowChat(true);
      }
    };

    openConversation();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);  // Run only once on mount — location.state is stable at mount time

  // Listen for real-time message events to update conversation list
  useEffect(() => {
    const unsubscribe = onMessageEvent((event) => {
      if (event.type === "NEW_MESSAGE" && event.message) {
        const msg = event.message;

        setConversations((prev) => {
          // Find existing conversation
          const existingIndex = prev.findIndex((c) => c.id === msg.conversationId);

          if (existingIndex >= 0) {
            // Update existing conversation and move to top
            const updated = [...prev];
            const conv = { ...updated[existingIndex] };
            conv.lastMessagePreview = msg.content.length > 200
              ? msg.content.substring(0, 200)
              : msg.content;
            conv.lastMessageSenderId = msg.senderId;
            conv.lastMessageAt = msg.createdAt;

            // Increment unread count if this message is from the other user
            // and we're NOT currently viewing that conversation
            if (msg.senderId !== user?.id && msg.conversationId !== selectedConversationId) {
              conv.unreadCount = conv.unreadCount + 1;
            }

            updated.splice(existingIndex, 1);
            updated.unshift(conv);
            return updated;
          } else {
            // New conversation — reload the list to get full data
            loadConversations(0);
            return prev;
          }
        });

        // If we had a pending recipient and a message was sent, update the conversationId
        if (pendingRecipient && event.message.senderId === user?.id) {
          setSelectedConversationId(msg.conversationId);
          setExplicitConversationId(msg.conversationId);
          setPendingRecipient(null);
          loadConversations(0);
        }
      }
    });

    return unsubscribe;
  }, [user, selectedConversationId, pendingRecipient, loadConversations]);

  // Handle selecting an existing conversation from the list
  const handleSelectConversation = (conversationId: number) => {
    setSelectedConversationId(conversationId);
    setExplicitConversationId(null);
    setPendingRecipient(null);
    setMobileShowChat(true);

    // Reset unread count for this conversation in the list AND immediately
    // update the header badge by computing the new total from local state.
    // This is synchronous — no REST round-trip needed — so the badge clears
    // the instant the user taps/clicks the conversation row.
    setConversations((prev) => {
      const updated = prev.map((c) =>
        c.id === conversationId ? { ...c, unreadCount: 0 } : c
      );
      const newTotal = updated.reduce((sum, c) => sum + c.unreadCount, 0);
      dispatchMessageEvent({
        type: "UNREAD_COUNT",
        message: null,
        conversationId: null,
        totalUnread: newTotal,
      });
      return updated;
    });
  };

  // Handle going back to list on mobile
  const handleBackToList = () => {
    setMobileShowChat(false);
    setSelectedConversationId(null);
    setExplicitConversationId(null);
    setPendingRecipient(null);
  };

  // Handle new conversation started from the search modal
  const handleConversationStarted = (conversationId: number, recipientInfo?: PendingRecipient) => {
    setShowNewMessage(false);
    setMobileShowChat(true);

    if (conversationId > 0) {
      /*
       * Backend found an existing conversation (possibly one that the user had
       * soft-deleted and therefore no longer appears in the local list).
       * We store both the conversationId and the recipient info so ConversationView
       * can load the message thread and display the header correctly, even before
       * the conversation list is reloaded.
       */
      setSelectedConversationId(conversationId);
      setExplicitConversationId(conversationId);
      setPendingRecipient(recipientInfo ?? null);
      // Reload so the soft-deleted conversation reappears in the left panel
      loadConversations(0);
    } else if (recipientInfo) {
      // No existing conversation — set pending recipient so ConversationView
      // can create one when the first message is sent
      setPendingRecipient(recipientInfo);
      setSelectedConversationId(null);
      setExplicitConversationId(null);
    }
  };

  // Called by ConversationView after first message creates a real conversation
  const handleConversationCreated = (conversationId: number) => {
    setSelectedConversationId(conversationId);
    setExplicitConversationId(conversationId);
    setPendingRecipient(null);
    loadConversations(0);
  };

  // Handle single conversation deletion
  const handleConversationDeleted = (conversationId: number) => {
    setConversations((prev) => prev.filter((c) => c.id !== conversationId));
    if (selectedConversationId === conversationId) {
      setSelectedConversationId(null);
      setExplicitConversationId(null);
      setPendingRecipient(null);
      setMobileShowChat(false);
    }
  };

  /**
   * Handle bulk deletion of multiple conversations.
   * Removes all deleted IDs from the local list in one pass.
   * If the currently open conversation was among the deleted ones, close it.
   */
  const handleBulkDelete = (deletedIds: number[]) => {
    const deletedSet = new Set(deletedIds);
    setConversations((prev) => prev.filter((c) => !deletedSet.has(c.id)));

    if (selectedConversationId && deletedSet.has(selectedConversationId)) {
      setSelectedConversationId(null);
      setExplicitConversationId(null);
      setPendingRecipient(null);
      setMobileShowChat(false);
    }
  };

  // Load more conversations
  const handleLoadMore = () => {
    if (page + 1 < totalPages) {
      loadConversations(page + 1);
    }
  };

  // ─── Inbox search handlers ───────────────────────────────────────────────

  /**
   * Execute the inbox-wide search. Replaces previous results with the first
   * page. Enforces minimum length client-side before issuing the request.
   */
  const handleSubmitSearch = useCallback(async () => {
    const trimmed = searchInput.trim();
    if (trimmed.length < SEARCH_MIN_CHARS) {
      setSearchError(t("messages_search_min_chars", { count: SEARCH_MIN_CHARS }));
      setIsSearchMode(true);
      setSearchResults([]);
      setSearchTotalItems(0);
      setSearchTotalPages(0);
      setSearchPage(0);
      setActiveSearchQuery("");
      return;
    }

    setSearchError(null);
    setSearchLoading(true);
    setIsSearchMode(true);
    try {
      const response = await searchInbox(trimmed, 0, CONVERSATIONS_PER_PAGE);
      setSearchResults(response.content);
      setSearchTotalItems(response.totalItems);
      setSearchTotalPages(response.totalPages);
      setSearchPage(0);
      setActiveSearchQuery(trimmed);
    } catch (err) {
      console.error("Inbox search failed:", err);
      setSearchError(t("messages_search_error_generic"));
      setSearchResults([]);
      setSearchTotalItems(0);
      setSearchTotalPages(0);
    } finally {
      setSearchLoading(false);
    }
  }, [searchInput, t]);

  /**
   * Append the next page of search results. Uses the active query (the one
   * that produced the current results), not whatever is in the input box
   * right now — so typing after searching doesn't corrupt pagination.
   */
  const handleLoadMoreSearch = useCallback(async () => {
    if (!activeSearchQuery) return;
    if (searchPage + 1 >= searchTotalPages || searchLoading) return;

    const nextPage = searchPage + 1;
    setSearchLoading(true);
    try {
      const response = await searchInbox(activeSearchQuery, nextPage, CONVERSATIONS_PER_PAGE);
      setSearchResults((prev) => [...prev, ...response.content]);
      setSearchPage(nextPage);
      setSearchTotalPages(response.totalPages);
    } catch (err) {
      console.error("Failed to load more search results:", err);
    } finally {
      setSearchLoading(false);
    }
  }, [activeSearchQuery, searchPage, searchTotalPages, searchLoading]);

  /**
   * Exit search mode: clear the input, results, and any errors. The normal
   * conversation list becomes visible again.
   */
  const handleClearSearch = () => {
    setSearchInput("");
    setIsSearchMode(false);
    setSearchResults([]);
    setSearchError(null);
    setSearchTotalItems(0);
    setSearchPage(0);
    setSearchTotalPages(0);
    setActiveSearchQuery("");
  };

  /**
   * Click on a search result row: open the underlying conversation in the
   * right panel. Keeps search mode active so the user can tap a different
   * result afterwards without retyping.
   */
  const handleSelectSearchResult = (result: MessageSearchResultDto) => {
    setMobileShowChat(true);

    // If the conversation exists in the loaded list, use the normal path —
    // that clears unread counters and dispatches the badge update.
    const existing = conversations.find((c) => c.id === result.conversationId);
    if (existing) {
      handleSelectConversation(result.conversationId);
      return;
    }

    /*
     * Conversation isn't in the local list (e.g. it's paginated out, or was
     * soft-deleted on this side). Fall back to the explicit-id pattern that
     * NewMessageModal uses for the re-open case, and also fire a reload so
     * the list catches up.
     */
    setSelectedConversationId(result.conversationId);
    setExplicitConversationId(result.conversationId);
    setPendingRecipient({
      userId: result.otherUserId,
      username: result.otherUsername,
      role: result.otherUserRole,
    });
    loadConversations(0);
  };

  // Get the selected conversation object from the loaded list
  const selectedConversation = conversations.find(
    (c) => c.id === selectedConversationId
  );

  /*
   * showChat: true when any of these is true:
   *   1. A conversation is found in the loaded list (normal flow)
   *   2. A pending new recipient is set (new conversation not yet created)
   *   3. An explicit conversationId was resolved by the modal but the conversation
   *      is not yet in the list (soft-deleted re-open case)
   */
  const showChat = !!(selectedConversation || pendingRecipient || explicitConversationId !== null);

  if (!user) {
    return (
      <div className="messages-page-auth-required">
        <p>{t("messages_login_required")}</p>
      </div>
    );
  }

  return (
    <div className="messages-page">
      {/* Conversation list — hidden on mobile when chat is open */}
      <div className={`messages-list-panel ${mobileShowChat ? "messages-hide-mobile" : ""}`}>
        <div className="messages-list-header">
          <h2>{t("messages_title")}</h2>
          <button
            className="messages-new-btn"
            onClick={() => setShowNewMessage(true)}
            title={t("messages_new")}
          >
            +
          </button>
        </div>
        <ConversationList
          conversations={conversations}
          selectedId={selectedConversationId}
          loading={loading}
          hasMore={page + 1 < totalPages}
          onSelect={handleSelectConversation}
          onLoadMore={handleLoadMore}
          onDelete={handleConversationDeleted}
          onBulkDelete={handleBulkDelete}
          search={{
            input: searchInput,
            onInputChange: setSearchInput,
            onSubmit: handleSubmitSearch,
            onClear: handleClearSearch,
            isActive: isSearchMode,
            results: searchResults,
            loading: searchLoading,
            error: searchError,
            totalItems: searchTotalItems,
            hasMore: searchPage + 1 < searchTotalPages,
            onLoadMore: handleLoadMoreSearch,
            onSelectResult: handleSelectSearchResult,
          } satisfies ConversationSearchState}
        />
      </div>

      {/* Chat view — hidden on mobile when list is visible */}
      <div className={`messages-chat-panel ${!mobileShowChat ? "messages-hide-mobile" : ""}`}>
        {showChat ? (
          <ConversationView
            /*
             * Priority order for conversationId:
             *   1. selectedConversation from list (normal case)
             *   2. explicitConversationId set by modal (soft-deleted re-open case)
             *   3. null (brand new conversation)
             */
            conversationId={selectedConversation?.id ?? explicitConversationId ?? null}
            /*
             * Priority order for display info:
             *   1. selectedConversation from list
             *   2. pendingRecipient (set by modal in both new and re-open cases)
             */
            otherUserId={selectedConversation?.otherUserId ?? pendingRecipient?.userId ?? 0}
            otherUsername={selectedConversation?.otherUsername ?? pendingRecipient?.username ?? ""}
            otherUserRole={selectedConversation?.otherUserRole ?? pendingRecipient?.role ?? ""}
            onBack={handleBackToList}
            onConversationCreated={handleConversationCreated}
            initialDraft={initialDraft ?? undefined}
            onDraftConsumed={() => setInitialDraft(null)}
          />
        ) : (
          <div className="messages-empty-state">
            <p>{t("messages_select_conversation")}</p>
          </div>
        )}
      </div>

      {/* New message modal */}
      {showNewMessage && (
        <NewMessageModal
          onClose={() => setShowNewMessage(false)}
          onConversationStarted={handleConversationStarted}
        />
      )}
    </div>
  );
};